import { ImageOff, Minus, Pencil, Plus, Trash2, TriangleAlert } from 'lucide-react'
import { Fragment, type ReactNode } from 'react'

import { Button, useSwipeDismiss } from '@krp/ui-kit'

import type { CartLineVm } from '../port/types'
import { PriceField } from './PriceField'

/**
 * Таблица чека — как в старом РМК: фото, товар, количество со степпером и
 * единицей, цена, сумма, «Удалить»; липкая шапка; прокручивается только она.
 * Строка выбирается щелчком — её крупно правит полоса сверху. С правом
 * `sales.price` цену можно поставить и прямо в строке (RP-FB15): список правится
 * там, где виден, не только выбранная позиция сверху. На узком экране
 * строки становятся карточками (CSS), сумма и «Удалить» уходят: удаление —
 * смахиванием влево и кнопкой в полосе правки (жест никогда не единственный способ).
 */
export function CartTable({
  lines,
  locked,
  canEditPrice = false,
  onSelect,
  onPriceFocus,
  onPrice,
  onPriceDone,
  onIncrement,
  onDecrement,
  onRemove,
  editor,
}: {
  lines: readonly CartLineVm[]
  locked: boolean
  canEditPrice?: boolean
  onSelect: (key: string) => void
  /** Фокус в поле цены строки: строка становится выбранной, но фокус остаётся в поле (в отличие от `onSelect`). */
  onPriceFocus?: (key: string) => void
  /** Цена строки решена (Enter или уход из поля). */
  onPrice?: (key: string, value: string) => void
  /** Enter в поле цены: фокус обратно в строку поиска (сканер печатает туда). */
  onPriceDone?: () => void
  onIncrement: (key: string) => void
  onDecrement: (key: string) => void
  onRemove: (key: string) => void
  /** Телефон: полоса правки выбранной строки — прямо под ней, а не отдельным блоком над чеком (аудит 15.10, п. 4). */
  editor?: ReactNode
}) {
  return (
    <table className="rmk-cart" aria-label="Позиции чека">
      <colgroup>
        <col className="rmk-cart__photo-column" />
        <col />
        <col className="rmk-cart__quantity-column" />
        <col className="rmk-cart__price-column" />
        <col className="rmk-cart__amount-column" />
        <col className="rmk-cart__action-column" />
      </colgroup>
      <thead>
        <tr>
          <th scope="col">
            <span className="visually-hidden">Фото</span>
          </th>
          <th scope="col">Товар</th>
          <th scope="col">Количество</th>
          <th scope="col">Цена</th>
          <th scope="col">Сумма</th>
          <th scope="col">
            <span className="visually-hidden">Действие</span>
          </th>
        </tr>
      </thead>
      <tbody>
        {lines.map((line) => (
          <Fragment key={line.key}>
            <CartRow
              line={line}
              locked={locked}
              canEditPrice={canEditPrice && onPrice !== undefined}
              onSelect={() => onSelect(line.key)}
              onPriceFocus={() => onPriceFocus?.(line.key)}
              onPrice={(value) => onPrice?.(line.key, value)}
              onPriceDone={onPriceDone}
              onIncrement={() => onIncrement(line.key)}
              onDecrement={() => onDecrement(line.key)}
              onRemove={() => onRemove(line.key)}
            />
            {line.selected && editor !== undefined && (
              <tr className="rmk-cart__editor-row">
                <td colSpan={6}>{editor}</td>
              </tr>
            )}
          </Fragment>
        ))}
      </tbody>
    </table>
  )
}

/**
 * Строка чека своим компонентом — ради жеста: смахивание влево удаляет позицию
 * (`useSwipeDismiss` ui-kit), подтверждающего окна нет — решение владельца
 * 23.09.2026 (карточка 264): строка исчезает рядом с пересчитанным итогом.
 * Пока идёт оплата, жест выключен, как и все правки чека.
 */
function CartRow({
  line,
  locked,
  canEditPrice,
  onSelect,
  onPriceFocus,
  onPrice,
  onPriceDone,
  onIncrement,
  onDecrement,
  onRemove,
}: {
  line: CartLineVm
  locked: boolean
  canEditPrice: boolean
  onSelect: () => void
  onPriceFocus: () => void
  onPrice: (value: string) => void
  onPriceDone?: () => void
  onIncrement: () => void
  onDecrement: () => void
  onRemove: () => void
}) {
  const swipe = useSwipeDismiss({ axis: 'left', onDismiss: onRemove, disabled: locked })
  return (
    <tr
      className="rmk-cart__row"
      aria-selected={line.selected}
      data-selected={line.selected || undefined}
      data-invalid={line.quantityError !== null || undefined}
      data-blocked={line.blocked.length > 0 || undefined}
      data-testid="rmk-cart-line"
      style={swipe.style}
      {...swipe.bind()}
      onClick={onSelect}
    >
      <td className="rmk-cart__thumb-cell">
        <span className="rmk-thumb" aria-hidden="true">
          <ImageOff size={16} />
        </span>
      </td>
      <td className="rmk-cart__name">
        <span title={line.name}>{line.name}</span>
        {line.quantityError !== null && (
          <small className="rmk-cart__error" role="alert">
            {line.quantityError}
          </small>
        )}
        {line.blocked.length > 0 && (
          <small className="rmk-cart__blocked" role="alert">
            <TriangleAlert size={13} aria-hidden="true" /> Не продаётся: {line.blocked.map((reason) => reason.text).join('; ')}
            <details>
              <summary>Подробнее</summary>
              <ul>
                {line.blocked.map((reason) => (
                  <li key={reason.code}>
                    {reason.fixBy === 'administrator' ? 'Исправляет администратор' : 'Исправляет кассир'} · {reason.code}
                    {reason.detail !== null && ` · ${reason.detail}`}
                  </li>
                ))}
              </ul>
            </details>
          </small>
        )}
      </td>
      <td className="rmk-cart__qty">
        <div className="rmk-stepper">
          <button
            type="button"
            aria-label={`Меньше: ${line.name}`}
            disabled={locked || line.isWeight}
            onClick={(event) => {
              event.stopPropagation()
              onDecrement()
            }}
          >
            <Minus size={16} aria-hidden="true" />
          </button>
          <span className="rmk-stepper__value">
            {line.quantity}
            {line.unit !== null && <span className="rmk-stepper__unit"> {line.unit}</span>}
          </span>
          <button
            type="button"
            aria-label={`Больше: ${line.name}`}
            disabled={locked || line.isWeight}
            onClick={(event) => {
              event.stopPropagation()
              onIncrement()
            }}
          >
            <Plus size={16} aria-hidden="true" />
          </button>
        </div>
      </td>
      <td className="rmk-cart__price" title={line.catalogPriceText ?? undefined}>
        {canEditPrice && !locked ? (
          // Поле внутри строки: щелчок и жест строки ему не нужны — иначе выбор строки уводит фокус в поиск,
          // а смахивание «удалить» перехватывает выделение текста.
          <span
            className="rmk-cart__price-edit"
            onClick={(event) => event.stopPropagation()}
            onPointerDown={(event) => event.stopPropagation()}
          >
            {line.price !== null && (
              // Своя цена — значок со словом в подсказке и для чтения с экрана, а не подпись под полем: она растила строку.
              <span className="rmk-cart__own-mark" role="img" aria-label="Своя цена" title="Своя цена">
                <Pencil size={12} aria-hidden="true" />
              </span>
            )}
            <PriceField
              className="rmk-stepper__input rmk-cart__price-input"
              saved={line.price ?? line.catalogPrice ?? ''}
              disabled={locked}
              invalid={line.priceError !== null}
              label={`Цена: ${line.name}`}
              onFocus={onPriceFocus}
              onCommit={onPrice}
              onDone={onPriceDone}
            />
          </span>
        ) : (
          <>
            {line.priceText ?? 'даст касса'}
            {line.price !== null && <span className="rmk-cart__own-price"> (своя)</span>}
          </>
        )}
      </td>
      <td className="rmk-cart__amount">{line.amountText ?? (line.quantityError !== null || line.priceError !== null ? '—' : 'пересчёт…')}</td>
      <td className="rmk-cart__action">
        <Button
          variant="ghost"
          icon={<Trash2 size={15} aria-hidden="true" />}
          aria-label={`Удалить: ${line.name}`}
          disabled={locked}
          onClick={(event) => {
            event.stopPropagation()
            onRemove()
          }}
        >
          <span className="rmk-cart__remove-text">Удалить</span>
        </Button>
      </td>
    </tr>
  )
}
